import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { captureAttribution } from './lib/growth'
import { applyTheme, readTheme } from './lib/theme'
import './index.css'
import App from './App.tsx'
import { Recovery } from './ui/Recovery'

// First-touch attribution, read while ?c= is still in the URL.
captureAttribution()
// A chosen theme applies before the first paint.
applyTheme(readTheme())

// F5: error reporting starts once the front page has loaded (lib/startReports.ts, its own download; no "sentry" in the
// file name, which ad-block lists drop); an error thrown before then (up to 50) is kept here and sent as soon as it
// starts. Sentry.ErrorBoundary below reports through the same client.
const early: unknown[] = []
const EARLY_MAX = 50 // a page throwing in a loop must not grow this without end
const keepError = (e: ErrorEvent) => { if (early.length < EARLY_MAX) early.push(e.error ?? e.message) }
const keepRejection = (e: PromiseRejectionEvent) => { if (early.length < EARLY_MAX) early.push(e.reason) }
window.addEventListener('error', keepError)
window.addEventListener('unhandledrejection', keepRejection)
let sentryStarted = false
function loadSentry() {
  if (sentryStarted) return
  sentryStarted = true
  void import('./lib/startReports').then(({ startSentry }) => {
    window.removeEventListener('error', keepError)
    window.removeEventListener('unhandledrejection', keepRejection)
    startSentry(early.splice(0))
  }).catch(() => { /* reporting is best-effort */ })
}
if (document.readyState === 'complete') loadSentry()
else window.addEventListener('load', loadSentry, { once: true })
window.setTimeout(loadSentry, 8000) // a photo that never finishes loading must not keep reporting off

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<Recovery />}>
      <App />
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
